import { EmptyListReason } from 'components/common/noTableContentInfo/NoTableContentInfo';
import React, {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useProjectOrganizationId } from 'store/project';
import { useProjectUsers, useProjectUsersActions } from 'store/projectUsers';
import { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import { SiteModel } from 'shared/domain/site/types/model';
import {
  userModelToUserModelLabelled,
  userModelToUserOnViewWithAccessLevel,
} from 'shared/domain/user/mapping/toView';
import { UserModel } from 'shared/domain/user/types/model';
import { ProcessType } from 'shared/domain/process/types/model';
import { findById } from 'shared/utils/id/findById';
import { createStrictOptionalContext } from 'helpers/context';
import { filterBySearchPhrase } from '../../common/SearchInput/helpers';
import { UsersContextType } from './types';

const userSearchableFields = ['label', 'email'];

const {
  Provider: UsersProvider,
  useRequiredContext: useUsers,
  useOptionalContext: useOptionalUsers,
} = createStrictOptionalContext<UsersContextType>('useUsers', 'WithUsers');

/**
 * The users of the selected project (loaded by `store/projectUsers`), with a search
 * phrase of its own per provider. Until PT-7121 moves its consumers to the
 * store hooks.
 */
const WithUsers: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { items: projectUsers, loading } = useProjectUsers();
  const { refresh: refreshUsers } = useProjectUsersActions();
  const users = useMemo(
    () => ({ items: projectUsers, total: projectUsers.length }),
    [projectUsers]
  );
  const intl = useIntl();
  const organizationId = useProjectOrganizationId();
  const [searchPhrase, setSearchPhrase] = useState<string>('');

  const filtered: UserModel[] = useMemo(() => {
    return filterBySearchPhrase(
      users.items.map((user) =>
        userModelToUserModelLabelled(user, organizationId)
      ),
      searchPhrase,
      userSearchableFields
    );
  }, [searchPhrase, users, organizationId]);

  const emptyListReason = useMemo((): EmptyListReason | undefined => {
    if (loading || users.total > 0) {
      return undefined;
    }
    return searchPhrase.length
      ? EmptyListReason.SEARCHPHRASE_MISMATCH
      : EmptyListReason.NOTHING_IN_DB;
  }, [users, searchPhrase, loading]);

  const getUserModelById = useCallback(
    (userId: string) => {
      return findById(users.items, userId, 'users context getUserModelById');
    },
    [users.items]
  );

  const getUserOnViewWithAccessLevelOnIconViewById = useCallback(
    (
      userId: string,
      userAccesses: UserAccess[],
      sites: SiteModel[],
      processes: ProcessType[]
    ) => {
      const userModel = getUserModelById(userId);
      if (!userModel) {
        return null;
      }

      return userModelToUserOnViewWithAccessLevel({
        userModel,
        userAccesses,
        sites: sites,
        processes: processes,
        intl,
        organizationId,
      });
    },
    [getUserModelById, intl, organizationId]
  );

  const ctx = useMemo(
    (): UsersContextType => ({
      filtered: { items: filtered, total: filtered.length },
      all: users,
      loading,
      emptyListReason,
      setSearchPhrase,
      refreshUsers,
      getUserOnViewWithAccessLevelOnIconViewById,
    }),
    [filtered, users, loading, emptyListReason, refreshUsers, getUserOnViewWithAccessLevelOnIconViewById]
  );

  return (
    <UsersProvider value={ctx}>{children}</UsersProvider>
  );
};

const withUsers =
  (Component: React.ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithUsers>
      <Component {...props} />
    </WithUsers>
  );

export { useOptionalUsers, useUsers, WithUsers, withUsers };
